import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  EmphasisTag,
  I18nText,
  BreadcrumbItem,
  Breadcrumbs,
} from '@welldev-group/react-comp-lib';
import Icon from '@welldev-group/rcl-icon';
import Card from '@welldev-group/rcl-card';
import routes from '../../../../shared/routes';
import httpClient from '../../../../shared/libraries/httpClient';
import planName from '../../../../shared/helper/planName';
import SupportTicketOverview from './SupportTicketOverview';
import AffiliateTabs from './AffiliateTabs';
import AffiliateAccountManager from './AffiliateAccountManager';
import ContactInfo from './ContactInfo';

const Affiliate = props => {
  const [affiliate, setAffiliate] = useState(null);

  const fetchAffiliate = async id => {
    const { data } = await httpClient.get(
      routes.affiliates.fetch({
        id,
      }),
    );
    setAffiliate(data.affiliate);
  };

  const updateAffiliate = async params => {
    const { data } = await httpClient.put(
      routes.affiliates.update({ id: props.id }),
      params,
    );

    if (data.error === null) {
      fetchAffiliate(props.id);
    }
  };

  useEffect(() => {
    fetchAffiliate(props.id);
  }, []);

  const showPlan = () => {
    switch (affiliate.currentPlanName) {
      case planName.FREE_PLAN:
        return (
          <EmphasisTag
            type="neutral"
            size="small"
            text={<I18nText id="plans.free" />}
          />
        );
      case planName.LITE_PLAN:
        return (
          <EmphasisTag
            type="null"
            size="small"
            text={<I18nText id="plans.lite" />}
          />
        );
      case planName.GROWTH_PLAN:
        return (
          <EmphasisTag
            type="accent"
            size="small"
            text={<I18nText id="plans.growth" />}
          />
        );
      case planName.ENTERPRISE_PLAN:
        return (
          <EmphasisTag
            type="important"
            size="small"
            text={<I18nText id="plans.enterprise" />}
          />
        );
      case planName.CUSTOM_PLAN:
        return (
          <EmphasisTag
            type="warning"
            size="small"
            text={<I18nText id="plans.custom" />}
          />
        );
      default:
        return null;
    }
  };

  return (
    affiliate && (
      <div className="affiliate__container container-full">
        <div>
          <div className="affiliate__name">
            {affiliate.clientName || `Affiliate ${props.id}`}
            <div className="affiliate__breadcrumbs">
              <Breadcrumbs>
                <BreadcrumbItem linkUrl={props.urls.affiliates}>
                  <I18nText id="team.form.placeholders.affiliate" />
                </BreadcrumbItem>
                <BreadcrumbItem linkUrl={props.urls.affiliate}>
                  {affiliate.clientName || `Affiliate ${props.id}`}
                </BreadcrumbItem>
              </Breadcrumbs>
            </div>
          </div>
          <div className="grid">
            <div className="col-3">
              <Card version="v2" size="full" className="affiliate__summary">
                <AffiliateAccountManager
                  accountManagerId={affiliate.accountManager.id}
                  accountManagerName={affiliate.accountManager.name}
                  accountManagerAvatar={affiliate.accountManager.avatar}
                  accountManagerTitle={affiliate.accountManager.title}
                  accountManagerOptions={affiliate.accountManager.options}
                  hasAccountManager={affiliate.hasAccountManager}
                  updateAffiliate={updateAffiliate}
                />
                <div className="affiliate__membership">
                  <div>
                    <div className="affiliate__membership-header">
                      {I18n.t(
                        'components.affiliate_three_sixty.affiliate.overview.membership',
                      )}
                    </div>
                    <div className="affiliate__membership-sub-header">
                      {I18n.t(
                        'components.affiliate_three_sixty.affiliate.overview.online',
                        {
                          year: moment(affiliate.createdAt).format('YYYY'),
                        },
                      )}{' '}
                    </div>
                    <div>{showPlan(affiliate.currentPlanName)}</div>
                  </div>
                  <div>
                    <Icon
                      size="large"
                      color="success"
                      name="achievement"
                      showBGColor={true}
                    />
                  </div>
                </div>
                <div className="affiliate__contact-info">
                  <div className="affiliate__contact-info-header">
                    {I18n.t(
                      'components.affiliate_three_sixty.affiliate.contact_information',
                    )}
                  </div>

                  <ContactInfo
                    contactMedia={affiliate.mobileNumber}
                    contactIcon="call"
                  />

                  <ContactInfo
                    contactMedia={affiliate.email}
                    contactIcon="email"
                  />
                </div>
                <div className="affiliate__support-overview">
                  <SupportTicketOverview affiliate={affiliate} />
                </div>
              </Card>
            </div>
            <div className="col-9">
              <AffiliateTabs
                id={props.id}
                fetchAffiliate={() => fetchAffiliate(props.id)}
                affiliate={affiliate}
                updateAffiliate={updateAffiliate}
                urls={props.urls}
                currentUserId={props.currentUserId}
                signedInAsSuperAdmin={props.signedInAsSuperAdmin}
                permissionTemplateOptions={props.permissionTemplateOptions}
                permissionOptions={props.permissionOptions}
                displayInvites={props.displayInvites}
                userAffiliateId={props.userAffiliateId}
              />
            </div>
          </div>
        </div>
      </div>
    )
  );
};

Affiliate.defaultProps = {
  permissionTemplateOptions: [],
};

Affiliate.propTypes = {
  id: PropTypes.number.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  urls: PropTypes.shape({
    affiliates: PropTypes.string.isRequired,
    affiliate: PropTypes.string.isRequired,
    users: PropTypes.string.isRequired,
    invites: PropTypes.string.isRequired,
    teams: PropTypes.string.isRequired,
  }).isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
  displayInvites: PropTypes.bool.isRequired,
};

export default Affiliate;
